/**
 * @file types.ts
 * @description Shared vocabulary for the atom layer (design library).
 * Atoms are the ONLY public source of rendering primitives: app code imports
 * from `@/components/atoms`, never `@/components/ui` or react-native UI
 * primitives. Atoms are the only components that accept `className`, and the
 * className vocabulary itself is closed by the Tailwind theme + lint.
 * See docs/design-system-rfc.md and docs/design-system/tokens.md.
 * @module components/atoms/types
 */

import type { View as RNView } from "react-native";

/**
 * Native view instance type for `measureInWindow` / `getBoundingClientRect`
 * refs. App code (molecules/organisms) types its trigger refs with this so it
 * never imports the lint-banned react-native `View` directly — the atom layer
 * owns the only sanctioned reference to the underlying primitive.
 */
export type ViewRef = RNView;

/** Ratified spacing steps (decision #3) — 4px grid + 2px micro + 1px hairline. */
export type SpaceToken =
  | "0"
  | "micro"
  | "px"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "8"
  | "10"
  | "12"
  | "16";

/**
 * Ratified text styles (decision #4) — 8 styles; `display` is one style with
 * a bundled size ladder. Each bundles size + line-height + family/weight.
 *
 * Naming (SE-6595): `-strong` is the single emphasis step above a base style,
 * as established by the `body` / `body-strong` pair. Where a size step needs a
 * SECOND weight beyond that pair, the face is named outright — `body-bold`
 * sits above `body-strong` (which is Medium), and `title-sm-medium` sits below
 * `title-sm` (which is Bold). An explicit face name is used only where
 * `-strong` is already taken at that size, so the suffix never means two
 * different weights.
 */
export type TextVariant =
  | "micro"
  | "micro-strong"
  | "caption"
  | "caption-strong"
  | "body"
  | "body-strong"
  | "body-bold"
  | "title-sm-medium"
  | "title-sm"
  | "title"
  | "title-lg"
  | "display-sm"
  | "display-md"
  | "display-lg"
  | "display-xl"
  | "display-2xl";

/**
 * The single sanctioned escape hatch (ratified decision #5): an RN style
 * object accepted by atoms only, greppable, counted in CI with a single-digit
 * budget. A style object cannot speak the banned className dialect.
 * Each atom types it as `WithUnsafeStyle<StyleOfWrappedComponent>`.
 */
export type WithUnsafeStyle<S> = {
  /** Escape hatch — counted in CI (budget ≤ 9 repo-wide). Prefer a token PR. */
  UNSAFE_style?: S;
};

/** className → gap class per spacing token (Stack space prop). */
export const GAP_CLASS: Record<SpaceToken, string> = {
  "0": "gap-0",
  micro: "gap-micro",
  px: "gap-px",
  "1": "gap-1",
  "2": "gap-2",
  "3": "gap-3",
  "4": "gap-4",
  "5": "gap-5",
  "6": "gap-6",
  "8": "gap-8",
  "10": "gap-10",
  "12": "gap-12",
  "16": "gap-16",
};

/**
 * className bundle per text variant. Size+line-height come from the fontSize
 * token; family/weight is bundled here (Satoshi regular/medium/bold ladder).
 *
 * The weight ladder is expressed as a FAMILY class, never a weight utility:
 * `font-body` → Satoshi, `font-medium` → SatoshiMedium, `font-heading` →
 * SatoshiBold. Each dedicated family carries exactly one face, so the bundle
 * cannot resolve to a synthesized weight. The emphasis bundles added by
 * SE-6595 use `font-heading` because that is what their call sites already
 * render: the shared `Satoshi` family registers 400/500/700 only, and CSS
 * Fonts L4 matches a target above 500 upward, so the raw weight utilities
 * those sites carried resolved to the real Bold face (measured in SE-5780 and
 * recorded in global.css). Bundling SatoshiMedium instead would have made the
 * migration a visible lightening rather than a like-for-like move.
 */
export const TEXT_VARIANT_CLASS: Record<TextVariant, string> = {
  micro: "text-micro font-body",
  "micro-strong": "text-micro font-heading",
  caption: "text-caption font-body",
  "caption-strong": "text-caption font-heading",
  body: "text-body font-body",
  "body-strong": "text-body font-medium",
  "body-bold": "text-body font-heading",
  "title-sm-medium": "text-title-sm font-medium",
  "title-sm": "text-title-sm font-heading",
  title: "text-title font-heading",
  "title-lg": "text-title-lg font-heading",
  "display-sm": "text-display-sm font-heading",
  "display-md": "text-display-md font-heading",
  "display-lg": "text-display-lg font-heading",
  "display-xl": "text-display-xl font-heading",
  "display-2xl": "text-display-2xl font-heading",
};

/**
 * Compose a text-style bundle onto a caller's className, for the text-entry
 * atoms (`Input`, `TextInput`) that route typography through a `textVariant`
 * prop instead of the `Text`/`Heading` `variant` prop.
 *
 * The `undefined` branch returns the caller's className BY IDENTITY — not a
 * rebuilt or re-joined string. That is the load-bearing property (SE-6596): a
 * text field renders no family class of its own today (the vendored
 * `inputFieldStyle` supplies only a size, and `TextInput` supplies nothing),
 * so its family is inherited. Emitting a bundle by default would flip every
 * input in the app onto Satoshi at once. Omitting the prop must therefore be a
 * true no-op, and identity is the strongest available proof of that.
 *
 * Bundle first, caller's className second — matching the `Text` atom — so a
 * caller can still override an individual axis of the bundle where the
 * cascade's last-wins order allows it.
 * @param variant Text style to apply, or `undefined` to inherit unchanged.
 * @param className The caller's own className, if any.
 * @returns The composed className, or the caller's className untouched.
 */
export const withTextVariant = (
  variant: TextVariant | undefined,
  className: string | undefined
): string | undefined => {
  if (variant === undefined) return className;
  const bundle = TEXT_VARIANT_CLASS[variant];
  return className === undefined || className === ""
    ? bundle
    : `${bundle} ${className}`;
};
